import dayjs from 'dayjs'
import { SHIFT, VISIT } from '../constants'
import { RateType } from '../hooks/usePaymentCards'
import type { TeamMemberRateDto } from '../hooks/useRevisionUpdate'
import {
  getDateTimeInZone,
  TIME_FORMAT,
  YEAR_FIRST_DATE_FORMAT
} from '../utils.datetime'

export type TeamMemberPayRateOverrideForm = {
  overridePayRate: boolean
  customRate?: number
  rateType?: RateType
}

export function normalizeTeamMemberFormIds(teamMembers: unknown): string[] {
  if (!Array.isArray(teamMembers)) {
    return []
  }
  return teamMembers.map((m: any) =>
    typeof m === 'object' && m != null && 'value' in m ? m.value : m
  )
}

export function getTeamMemberDisplayName(
  teamMemberId: string,
  formTeamMembers: unknown,
  revisionTeamMembers: any[] | undefined
): string {
  if (Array.isArray(formTeamMembers)) {
    const fromForm = formTeamMembers.find((m: any) =>
      typeof m === 'object' && m != null && 'value' in m
        ? m.value === teamMemberId
        : m === teamMemberId
    )
    if (
      fromForm &&
      typeof fromForm === 'object' &&
      'label' in fromForm &&
      fromForm.label
    ) {
      return String(fromForm.label)
    }
  }
  const fromRev = revisionTeamMembers?.find(
    (m: any) => m.teamMemberId === teamMemberId
  )
  const n = fromRev?.carer?.name
  if (n?.first || n?.last) {
    return `${n.first ?? ''} ${n.last ?? ''}`.trim()
  }
  return teamMemberId
}

export function getRevisionTeamMembersSource(values: any): any[] | undefined {
  const list = values?.teamMembers ?? values?.changes?.teamMembers
  return Array.isArray(list) ? list : undefined
}

function parseMemberRateType(
  raw: unknown,
  nested: Record<string, unknown> | null | undefined
): RateType {
  const fromNested = nested?.payRateType ?? nested?.rateType ?? nested?.type
  const typeRaw = fromNested ?? raw
  if (
    typeRaw === RateType.EVENT ||
    typeRaw === 'EVENT' ||
    typeRaw === 'FIXED'
  ) {
    return RateType.EVENT
  }
  return RateType.HOURLY
}

export function buildTeamMemberPayRateOverridesInitial(
  teamMembers: any[] | undefined
): Record<string, TeamMemberPayRateOverrideForm> {
  const record: Record<string, TeamMemberPayRateOverrideForm> = {}
  for (const m of teamMembers ?? []) {
    const id = m?.teamMemberId
    if (!id) {
      continue
    }
    const po = m?.payRateOverride
    const rateStr =
      po != null && typeof po === 'object' && po.rate != null && po.rate !== ''
        ? String(po.rate)
        : m?.rate != null && m?.rate !== ''
          ? String(m.rate)
          : undefined
    const hasRate = rateStr != null && !Number.isNaN(Number(rateStr))
    const overridePayRate = Boolean(po) || hasRate
    const rateType = parseMemberRateType(m?.rateType, po)

    record[id] = {
      overridePayRate,
      customRate: hasRate ? Number(rateStr) : undefined,
      rateType
    }
  }
  return record
}

export function buildTeamMemberRatesPayload(
  overrides: Record<string, TeamMemberPayRateOverrideForm> | undefined,
  assignedTeamMemberIds: string[]
): TeamMemberRateDto[] {
  return assignedTeamMemberIds.map((teamMemberId) => {
    const o = overrides?.[teamMemberId]
    if (
      !o?.overridePayRate ||
      o.customRate == null ||
      Number.isNaN(Number(o.customRate))
    ) {
      return { teamMemberId, rateType: undefined }
    }
    const n = Number(o.customRate)
    return {
      teamMemberId,
      rate: n.toFixed(2),
      rateType: o.rateType
    }
  })
}

export const getRevisionFormInitialValues = (values: any) => {
  const override = values?.invoicingRateOverride
  return {
    ...values,
    slotNotes: values?.description,
    // utc() is called here to normalize antd date/time picker to understand correct behaviour, to avoid current user zone effect
    patternStart: getDateTimeInZone(values?.startTime).utc(),
    type: values?.siteShift === true ? SHIFT : VISIT,
    startTime: dayjs(values?.startTime).utc().format(TIME_FORMAT),
    endTime: dayjs(values?.endTime).utc().format(TIME_FORMAT),
    // Invoicing rate override fields
    overrideInvoicingRate: !!override,
    invoicingRateCardId:
      override?.rateCardId ??
      (override?.customRate != null ? '__custom__' : undefined),
    customInvoicingRate: override?.customRate ?? undefined
  }
}

export const getRevisionSubmitValues = (
  values: any,
  isTimeTouched?: boolean | undefined,
  isSiteShift?: boolean | undefined
) => {
  // Build invoicingRateOverride from the override form fields
  let invoicingRateOverride:
    | { rateCardId?: string; customRate?: number; unit?: string }
    | null
    | undefined
  if ('overrideInvoicingRate' in values) {
    if (!values.overrideInvoicingRate) {
      invoicingRateOverride = null // explicitly remove override
    } else if (values.invoicingRateCardId === '__custom__') {
      invoicingRateOverride = {
        customRate: values.customInvoicingRate,
        unit: 'HOUR'
      }
    } else if (values.invoicingRateCardId) {
      invoicingRateOverride = { rateCardId: values.invoicingRateCardId }
    }
    // defensive: override toggled on but no rate card resolved — should be caught by form
    // validation, but treat as no override rather than silently sending undefined
    // (invoicingRateOverride remains undefined → field omitted from payload → no change)
  }

  const transformedValues = {
    title: values?.title,
    date:
      values?.patternStart &&
      dayjs(values?.patternStart).format(YEAR_FIRST_DATE_FORMAT),
    ...(isTimeTouched &&
      values?.startTime && {
        startTime: values?.startTime
      }),
    ...(isTimeTouched &&
      values?.endTime && {
        endTime: values?.endTime
      }),
    flexiType: values?.flexiType,
    plannedDuration: values?.plannedDuration,
    addressId: isSiteShift ? undefined : values?.addressId,
    fundingTypeId: values?.fundingTypeId,
    description: values?.slotNotes,
    instanceTypeId: values?.instanceTypeId,
    ...(invoicingRateOverride !== undefined && { invoicingRateOverride })
  }

  return transformedValues
}

export const getRevisionReqFormInitialValues = (values: any) => {
  const teamMembersSource = getRevisionTeamMembersSource(values)
  const mappedTeamMembers = teamMembersSource?.map((member: any) => ({
    label:
      member.carer?.name?.first && member.carer?.name?.last
        ? `${member.carer.name.first} ${member.carer.name.last}`
        : undefined,
    value: member.teamMemberId || undefined
  }))
  return {
    ...values,
    slotNotes: values?.description,
    type: values?.siteShift === true ? 'shift' : 'visit',
    teamMembers: mappedTeamMembers,
    teamMemberPayRateOverrides:
      buildTeamMemberPayRateOverridesInitial(teamMembersSource)
  }
}
